import { __awaiter, __decorate } from "tslib";
import { Events } from '@ag-grid-community/core';
import { Connect } from '@genesislcap/foundation-comms';
import { GRID_READY_EVENT, } from '@genesislcap/foundation-zero-grid-pro';
import { EDITED_COLUMN_FIELD, ERROR_COLUMN_FIELD } from '@genesislcap/grid-pro';
import { attr, customElement, FASTElement, html, observable } from '@microsoft/fast-element';
import { buttonCellRenderer } from '../utils';
import { listStyles } from './list.styles';
import { listTemplate } from './list.template';
let List = class List extends FASTElement {
    constructor() {
        super(...arguments);
        this.resourceName = '';
        this.title = '';
        this.asyncAdd = false;
        this.asyncRemove = false;
        this.asyncUpdate = true;
        this.enableCellFlashing = false;
        this.enableRowFlashing = false;
        this.hideEdit = false;
        this.hideDelete = false;
        this.actionButtonsConfig = [];
        this.edit = (e) => {
            this.$emit('edit-entity', e);
        };
    }
    connectedCallback() {
        super.connectedCallback();
        this.grid.addEventListener(GRID_READY_EVENT, () => {
            this.grid.gridApi.addEventListener(Events.EVENT_FIRST_DATA_RENDERED, () => {
                if (this.sizeColumnsToFit) {
                    this.grid.gridApi.sizeColumnsToFit();
                }
            });
            this.grid.gridApi.addEventListener(Events.EVENT_ROW_CLICKED, (e) => this.select(e));
            if (this.updateEvent) {
                this.grid.gridApi.addEventListener(Events.EVENT_CELL_EDIT_REQUEST, (e) => this.onCellEditingStopped(e));
            }
            this.actionButtonsConfig = [];
            if (this.updateEvent && !this.hideEdit) {
                this.actionButtonsConfig.push(buttonCellRenderer('Edit', this.edit, html `
              <zero-icon
                style="pointer-events: none; opacity: 0.5"
                class="icon-button"
                variant="solid"
                name="pen"
                size="lg"
              ></zero-icon>
            `));
            }
            if (this.deleteEvent && !this.hideDelete) {
                this.actionButtonsConfig.push(buttonCellRenderer('Delete', (e) => this.delete(e), html `
              <zero-icon
                style="opacity: 0.5"
                variant="regular"
                name="trash-alt"
                size="lg"
              ></zero-icon>
            `));
            }
            if (this.enableFilterBar) {
                this.addEventListener('filter-changed', this.handleFilterChanged);
                this.addEventListener('filter-cleared', this.handleFilterCleared);
            }
        });
    }
    onCellEditingStopped(event) {
        var _a, _b;
        return __awaiter(this, void 0, void 0, function* () {
            const jsonSchemaResponse = yield this.connect.getJSONSchema(this.updateEvent);
            const jsonSchema = (_b = (_a = jsonSchemaResponse === null || jsonSchemaResponse === void 0 ? void 0 : jsonSchemaResponse.INBOUND) === null || _a === void 0 ? void 0 : _a.properties) === null || _b === void 0 ? void 0 : _b.DETAILS;
            const editedField = event.column.getColDef().field;
            event.node.setData(Object.assign(Object.assign({}, event.data), { [ERROR_COLUMN_FIELD]: undefined, [EDITED_COLUMN_FIELD]: editedField, [editedField]: event.newValue }));
            // copy only properties required by schema
            const payload = Object.fromEntries(Object.keys(jsonSchema.properties).map((key) => [
                key,
                // if column config was provided get data using grid api, so it takes into account
                // methods like valueGetter
                event.api.getColumnDef(key) ? event.api.getValue(key, event.node) : event.data[key],
            ]));
            const updateReq = yield this.connect.commitEvent(this.updateEvent, {
                // merge current payload with the newly edited cell value
                DETAILS: Object.assign(Object.assign({}, payload), { [editedField]: event.newValue }),
            });
            if (updateReq.ERROR) {
                this.$emit('submit-failure', { payload, errors: updateReq.ERROR });
                event.node.setData(Object.assign(Object.assign({}, event.data), { [editedField]: event.oldValue, [EDITED_COLUMN_FIELD]: '', [ERROR_COLUMN_FIELD]: {
                        column: editedField,
                        message: updateReq.ERROR.map((error) => `${error.CODE}: ${error.TEXT}`),
                    } }));
            }
            else {
                event.node.setData(Object.assign(Object.assign({}, event.data), { [editedField]: event.newValue, [EDITED_COLUMN_FIELD]: '' }));
                this.$emit('submit-success', { payload });
            }
        });
    }
    disconnectedCallback() {
        super.disconnectedCallback();
        if (this.enableFilterBar) {
            this.removeEventListener('filter-changed', this.handleFilterChanged);
            this.removeEventListener('filter-cleared', this.handleFilterCleared);
        }
    }
    handleFilterChanged(e) {
        var _a, _b;
        const fieldName = (_a = e === null || e === void 0 ? void 0 : e.detail) === null || _a === void 0 ? void 0 : _a.fieldName;
        const filter = (_b = e === null || e === void 0 ? void 0 : e.detail) === null || _b === void 0 ? void 0 : _b.filter;
        fieldName && filter && this.datasource.setFilter(fieldName, filter);
    }
    handleFilterCleared(e) {
        var _a;
        const fieldName = (_a = e === null || e === void 0 ? void 0 : e.detail) === null || _a === void 0 ? void 0 : _a.fieldName;
        this.datasource.removeFilter(fieldName);
    }
    select(e) {
        this.$emit('rowSelected', e);
    }
    delete(e) {
        return __awaiter(this, void 0, void 0, function* () {
            this.$emit('delete-entity', e);
        });
    }
};
__decorate([
    attr
], List.prototype, "resourceName", void 0);
__decorate([
    attr
], List.prototype, "title", void 0);
__decorate([
    attr({ attribute: 'persist-column-state-key' })
], List.prototype, "persistColumnStateKey", void 0);
__decorate([
    attr({ mode: 'boolean', attribute: 'size-columns-to-fit' })
], List.prototype, "sizeColumnsToFit", void 0);
__decorate([
    attr({ mode: 'boolean', attribute: 'enable-filter-bar' })
], List.prototype, "enableFilterBar", void 0);
__decorate([
    attr({ mode: 'boolean', attribute: 'async-add' })
], List.prototype, "asyncAdd", void 0);
__decorate([
    attr({ mode: 'boolean', attribute: 'async-remove' })
], List.prototype, "asyncRemove", void 0);
__decorate([
    attr({ mode: 'boolean', attribute: 'async-update' })
], List.prototype, "asyncUpdate", void 0);
__decorate([
    attr({ mode: 'boolean', attribute: 'enable-cell-flashing' })
], List.prototype, "enableCellFlashing", void 0);
__decorate([
    attr({ mode: 'boolean', attribute: 'enable-row-flashing' })
], List.prototype, "enableRowFlashing", void 0);
__decorate([
    attr({ mode: 'boolean', attribute: 'hide-edit' })
], List.prototype, "hideEdit", void 0);
__decorate([
    attr({ mode: 'boolean', attribute: 'hide-delete' })
], List.prototype, "hideDelete", void 0);
__decorate([
    observable
], List.prototype, "gridOptions", void 0);
__decorate([
    observable
], List.prototype, "columns", void 0);
__decorate([
    observable
], List.prototype, "datasourceConfig", void 0);
__decorate([
    observable
], List.prototype, "actionButtonsConfig", void 0);
__decorate([
    observable
], List.prototype, "deleteEvent", void 0);
__decorate([
    observable
], List.prototype, "updateEvent", void 0);
__decorate([
    Connect
], List.prototype, "connect", void 0);
List = __decorate([
    customElement({
        name: 'entity-list',
        template: listTemplate,
        styles: listStyles,
    })
], List);
export { List };
